// @ts-nocheck
/**
 * Insights Page Component
 * Displays stakeholder analytics with matrix, trends, connectors, and network visualizations
 * Layout: 2x2 grid matching admin portal
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  CategoryScale,
} from 'chart.js';
import { api } from '../api';
import { useScreenOrientation } from '../hooks/useScreenOrientation';
import useViewportSize from '../hooks/useViewportSize';
import LandscapePrompt from '../components/LandscapePrompt';
import './Insights.css';

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale);

interface MatrixPoint {
  id: string;
  name: string;
  influence: number;
  interest: number;
  relationship: string;
  category: string;
  profile_image: string | null;
}

interface TrendItem {
  id: string;
  name: string;
  change: number;
}

interface Connector {
  id: string;
  name: string;
  connections: number;
  influence: number;
  interest: number;
  profile_image: string | null;
}

interface NetworkNode {
  id: string;
  name: string;
  type?: string;
  influence_level?: number;
  interest_level?: number;
  relationship?: string;
}

interface NetworkEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  relationship?: string;
  influence_level?: number;
  interest_level?: number;
}

interface InsightsData {
  matrix: MatrixPoint[];
  trends: {
    warming: number;
    cooling: number;
    stale: number;
    details: {
      warming: TrendItem[];
      cooling: TrendItem[];
      stale: TrendItem[];
    };
  };
  key_connectors: Connector[];
  network: {
    nodes: NetworkNode[];
    edges: NetworkEdge[];
  };
}

// Jitter function from admin portal MatrixCard
const createJitter = (id: string, maxOffset = 0.15) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const x = ((hash & 0xFFFF) / 0xFFFF - 0.5) * 2;
  const y = (((hash >> 16) & 0xFFFF) / 0xFFFF - 0.5) * 2;
  
  return {
    x: x * maxOffset,
    y: y * maxOffset
  };
};

// Get color based on relationship
const getStanceColor = (relationship: string) => {
  const rel = (relationship || '').toLowerCase();
  if (rel.includes('support')) return 'rgba(34, 197, 94, 0.9)'; // Green
  if (rel.includes('neutral')) return 'rgba(245, 158, 11, 0.9)'; // Amber/Yellow
  if (rel.includes('oppos') || rel.includes('against')) return 'rgba(239, 68, 68, 0.9)'; // Red
  return 'rgba(107, 114, 128, 0.9)'; // Gray (unknown)
};

const Insights: React.FC = () => {
  const orientation = useScreenOrientation();
  const viewport = useViewportSize();
  const [data, setData] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const networkRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    loadInsights();
  }, []);

  useEffect(() => {
    if (data) {
      renderNetwork();
    }
  }, [data]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getInsights();
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  // Prepare Chart.js data with jitter
  const chartData = useMemo(() => {
    if (!data || !data.matrix) return { datasets: [] };

    const jitteredData = data.matrix.map(d => {
      const jitter = createJitter(d.id || d.name, 0.25);
      
      const jitteredX = Math.max(0.5, Math.min(5.5, (d.interest || 3) + jitter.x));
      const jitteredY = Math.max(0.5, Math.min(5.5, (d.influence || 3) + jitter.y));
      
      return {
        x: jitteredX,
        y: jitteredY,
        _raw: d
      };
    });

    return {
      datasets: [
        {
          label: 'Stakeholders',
          data: jitteredData,
          pointBackgroundColor: jitteredData.map(d => getStanceColor(d._raw.relationship)),
          pointBorderColor: 'rgba(255, 255, 255, 0.8)',
          pointBorderWidth: 2,
          pointRadius: 8,
          pointHoverRadius: 10,
          pointStyle: 'circle',
        }
      ]
    };
  }, [data]);

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        min: 0.5,
        max: 5.5,
        ticks: {
          callback: function(value: any) {
            // Only show integer labels 1-5
            return [1, 2, 3, 4, 5].includes(value) ? value : '';
          },
          autoSkip: false,
          maxTicksLimit: 11 // Allow ticks at 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: (context: any) => {
            // Thicker lines at cell boundaries (0.5, 1.5, 2.5, 3.5, 4.5, 5.5)
            // Thinner lines at label positions (1, 2, 3, 4, 5)
            const value = context.tick.value;
            if ([0.5, 1.5, 2.5, 3.5, 4.5, 5.5].includes(value)) {
              return 2; // Thick lines for cell boundaries
            }
            return 0.5; // Thin lines for label positions
          },
        },
        title: {
          display: true,
          text: 'Interest Level',
          font: { size: 12 }
        }
      },
      y: {
        type: 'linear',
        min: 0.5,
        max: 5.5,
        ticks: {
          callback: function(value: any) {
            // Only show integer labels 1-5
            return [1, 2, 3, 4, 5].includes(value) ? value : '';
          },
          autoSkip: false,
          maxTicksLimit: 11 // Allow ticks at 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: (context: any) => {
            // Thicker lines at cell boundaries (0.5, 1.5, 2.5, 3.5, 4.5, 5.5)
            // Thinner lines at label positions (1, 2, 3, 4, 5)
            const value = context.tick.value;
            if ([0.5, 1.5, 2.5, 3.5, 4.5, 5.5].includes(value)) {
              return 2; // Thick lines for cell boundaries
            }
            return 0.5; // Thin lines for label positions
          },
        },
        title: {
          display: true,
          text: 'Influence Level',
          font: { size: 12 }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const point = context.raw;
            const stakeholder = point._raw;
            return [
              stakeholder.name,
              `Influence: ${stakeholder.influence}`,
              `Interest: ${stakeholder.interest}`,
              `Relationship: ${stakeholder.relationship}`
            ];
          }
        }
      }
    }
  };

  const renderNetwork = () => {
    if (!networkRef.current || !data || !data.network) return;

    const svg = d3.select(networkRef.current);
    svg.selectAll('*').remove();

    // Get responsive dimensions from container
    const container = networkRef.current.parentElement;
    const width = container ? container.clientWidth : 600;
    const height = 400; // Fixed height, but responsive width

    svg.attr('width', width).attr('height', height);

    // Create a group for zoom/pan transformations
    const g = svg.append('g');

    const nodes = data.network.nodes.map(d => ({ ...d }));
    const links = data.network.edges.map(d => ({ ...d }));

    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    // Zoom behavior - enables pinch-to-zoom and pan
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3]) // Min and max zoom levels
      .on('zoom', (event: any) => {
        g.attr('transform', event.transform);
      });

    // Apply zoom behavior to SVG
    svg.call(zoom as any);

    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6);

    // Drag functions for nodes
    const drag = d3.drag()
      .on('start', (event: any, d: any) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event: any, d: any) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event: any, d: any) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    // Create node groups
    const nodeGroup = g.append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .style('cursor', 'grab')
      .call(drag as any);

    // Add circles to node groups
    nodeGroup.append('circle')
      .attr('r', (d: any) => d.type === 'client' ? 15 : 10)
      .attr('fill', (d: any) => {
        if (d.type === 'client') return '#3b82f6';
        return getStanceColor(d.relationship || 'unknown');
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    // Add tooltips to node groups
    nodeGroup.append('title')
      .text((d: any) => {
        if (d.type === 'client') return `${d.name || 'Client'}\nType: Client`;
        return `${d.name}\nInfluence: ${d.influence_level || 'N/A'}\nInterest: ${d.interest_level || 'N/A'}\nRelationship: ${d.relationship || 'Unknown'}`;
      });

    const label = g.append('g')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text((d: any) => d.name)
      .attr('font-size', '10px')
      .attr('dx', 12)
      .attr('dy', 4)
      .attr('fill', '#1a202c');

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeGroup
        .attr('transform', (d: any) => `translate(${d.x},${d.y})`);

      label
        .attr('x', (d: any) => d.x)
        .attr('y', (d: any) => d.y);
    });
  };

  if (loading) {
    return (
      <div className="insights-container">
        <div className="insights-header">
          <h1>Insights</h1>
          <p>Stakeholder relationship trends and analytics</p>
        </div>
        <div className="loading-state">Loading insights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-container">
        <div className="insights-header">
          <h1>Insights</h1>
          <p>Stakeholder relationship trends and analytics</p>
        </div>
        <div className="error-state">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="insights-container">
      {/* Landscape orientation prompt for mobile users in portrait mode */}
      {orientation === 'portrait' && viewport.w < 1024 && (
        <LandscapePrompt pageName="insights" />
      )}

      <div className="insights-header">
        <h1>Insights</h1>
        <p>Stakeholder relationship trends and analytics</p>
      </div>

      <div className="insights-grid">
        {/* Matrix Card - Top Left */}
        <div className="insights-card matrix-card">
          <h3>Matrix (Interest × Influence)</h3>
          <div className="chart-container">
            <Scatter data={chartData} options={chartOptions} />
          </div>
          <div className="legend">
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: 'rgba(34, 197, 94, 0.9)' }}></span>
              Supportive
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: 'rgba(245, 158, 11, 0.9)' }}></span>
              Neutral
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: 'rgba(239, 68, 68, 0.9)' }}></span>
              Opposed
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: 'rgba(107, 114, 128, 0.9)' }}></span>
              Unknown
            </span>
          </div>
        </div>

        {/* Trends Card - Top Right */}
        <div className="insights-card trends-card">
          <h3>Trends (Warming/Cooling/Stale)</h3>
          <div className="trends-grid">
            <div className="trend-box warming">
              <div className="trend-icon">↑</div>
              <div className="trend-label">Warming</div>
              <div className="trend-count">{data?.trends.warming || 0}</div>
            </div>
            <div className="trend-box cooling">
              <div className="trend-icon">↓</div>
              <div className="trend-label">Cooling</div>
              <div className="trend-count">{data?.trends.cooling || 0}</div>
            </div>
            <div className="trend-box stale">
              <div className="trend-icon">—</div>
              <div className="trend-label">Stale</div>
              <div className="trend-count">{data?.trends.stale || 0}</div>
            </div>
          </div>
          <div className="trend-details">
            {data?.trends.details.warming.slice(0, 3).map(item => (
              <div key={item.id} className="trend-item">
                <span className="trend-name">{item.name}</span>
                <span className="trend-change positive">↑ {Math.round(item.change * 100)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Network Card - Bottom Left */}
        <div className="insights-card network-card">
          <h3>Network Map</h3>
          <div className="network-container">
            <svg ref={networkRef}></svg>
          </div>
          <div className="network-info">
            {data?.network.nodes.length || 0} stakeholders, {data?.network.edges.length || 0} connections
          </div>
        </div>

        {/* Key Connectors Card - Bottom Right */}
        <div className="insights-card connectors-card">
          <h3>Key Connectors</h3>
          <div className="connectors-list">
            {data?.key_connectors.slice(0, 5).map((connector, index) => (
              <div key={connector.id} className="connector-item">
                <div className="connector-rank">{index + 1}</div>
                <div className="connector-avatar">
                  {connector.profile_image ? (
                    <img src={connector.profile_image} alt={connector.name} />
                  ) : (
                    <div className="avatar-placeholder">{connector.name.charAt(0)}</div>
                  )}
                </div>
                <div className="connector-info">
                  <div className="connector-name">{connector.name}</div>
                  <div className="connector-stats">
                    {connector.connections} connections · I:{connector.influence}/I:{connector.interest}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
